import React from "react";

const ART = "/images/Home/production-house";

/**
 * "Zero-Day & Exploit Production House" illustration.
 *
 * Two separate compositions, both straight from Figma, both drawn as one inline
 * SVG in the designer's own coordinate space so every asset keeps its exact
 * position and the labels scale with the artwork:
 *
 *   lg and up — node 2601:58447, the 1292.14 × 240.61 band inside Banner_1: one
 *   wide row, research on the left, the Exploit Intelligence core in the middle,
 *   outputs and products on the right.
 *
 *   below lg — node 2612:38704 (iPhone 13/14, 390 × 844), which is a genuinely
 *   different layout rather than a squeeze of the desktop one: the pipeline runs
 *   top to bottom, the core loses its right-hand arrow plate to a vertical
 *   ↓↓↓ group, and the output chips fan out below the core. Capped at the design
 *   width (plus a little at sm/md) so the phone layout is never scaled up beyond
 *   what its 8px chip labels can take.
 *
 * Motion (see the <style jsx> block at the bottom) animates the pipeline the
 * diagram already draws: research feeds the core, the core dispatches to three
 * outputs, the outputs reach three products. Two rules hold it together:
 *
 *   Nothing new is drawn. Every animated element is either a piece Figma already
 *   put on the board or a marker, and a marker is invisible when it is not
 *   moving — so the resting frame is pixel-identical to the static design.
 *
 *   Everything is gated on `svg[data-playing="true"]`. The three banners are all
 *   in the DOM at once and cross-fade, so without the gate this would burn frames
 *   under a hidden slide and the viewer would always catch it mid-cycle. It also
 *   means the reduced-motion and not-yet-active states need no separate styling:
 *   no animation at all is already the finished frame.
 */

/** Figma frame for the desktop band. */
const BAND_W = 1292.14;
const BAND_H = 240.61;

/**
 * Figma frame for the phone composition. The viewBox is a window onto the 390 ×
 * 844 artboard covering just the diagram (y 150 → 615), so x coordinates stay
 * identical to Figma.
 */
const M_VIEWBOX = "0 150 390 465";

const ART_LABEL =
  "ECQ exploit intelligence pipeline: SHRED and KhaiCode 0-day and n-day research feeds " +
  "Exploit Intelligence, which supplies Exploit Pack, attack scenarios and detection " +
  "engineering to Q-Suite, HackGrid Cyber Range and MDR/SOC.";

/**
 * Where each core artwork sits, and where its dial centre lands inside that box.
 *
 * `core.svg` and `core-mobile.svg` ship as one flat Figma export apiece — 170 and
 * 162 sibling paths, no groups that mean anything, loaded through <image href>
 * where CSS cannot reach them. scripts/split-core-svg.mjs cuts each one into the
 * paint-order runs listed in CORE_LAYERS; stacking those layers in that order is
 * pixel-identical to the original (verified at 4x, zero differing pixels).
 *
 * `dial` / `arc` are `transform-origin` percentages of the <image> box, which is
 * the one origin form that survives both compositions: the phone core is not a
 * uniform scale of the desktop one (0.639 across, 0.709 down), so shared user-unit
 * origins would be wrong, and percentages of fill-box are measured per element.
 * Both were fitted to the artwork rather than eyeballed — `dial` is the common
 * centre of the nine concentric lens circles, `arc` a least-squares circle through
 * the red arcs (residual 1.1 units desktop, 0.7 mobile).
 */
const CORE = {
  box: { x: 420, y: 0, w: 383.155, h: 240.617 },
  dial: "56.05% 50.04%",
  arc: "56.71% 49.48%",
  /** Dial-tick ring, in band coordinates: centre (634.75, 120.4), radius 25.95. */
  tickArc: "M635.57 94.46A25.95 25.95 0 1 0 635.61 146.34",
  /** Charge width, matched to the 3.5-unit band the ticks occupy here. */
  dialWidth: 3.6,
};

const M_CORE = {
  box: { x: 42.48, y: 300.97, w: 244.815, h: 170.537 },
  dial: "62.58% 50.49%",
  arc: "63.31% 50.06%",
  /** Same ring on the phone core: centre (195.68, 387.07), radius 18.55. */
  tickArc: "M196.20 368.53A18.55 18.55 0 1 0 196.26 405.61",
  /* The phone ticks occupy a 2.5-unit band against the desktop's 3.5, the same
     0.714 the two radii sit at, so the charge is scaled to match rather than
     reused at desktop weight. */
  dialWidth: 2.6,
};

/** Paint order. Ids not covered here are transcribed into the JSX below. */
const CORE_LAYERS = [
  "rings", //     grey bezel arcs
  "arcs", //      red arcs — the only concentric ones, so the only ones that turn
  "plate", //     arrow plate + red hook + the EXPLOIT INTELLIGENCE plate + lens
  "ticks", //     90 dial ticks
  "reticle", //   crosshair
] as const;

/**
 * Marker rays. These are not new lines — they retrace the connector artwork so a
 * marker can travel it, and they are drawn with no stroke of their own.
 *
 * `chipBox` is the placement box of the output chip the ray runs through. The
 * marker does not cross the label: it vanishes at the chip's leading point, the
 * chip's border flashes, and it reappears on the line beyond the trailing point.
 * `tap` is the same idea for the feed ray, where the 0-day / n-day chips only
 * straddle the line rather than sit on it, so nothing has to be hidden.
 */
type Ray = {
  key: string;
  pts: [number, number][];
  /** Seconds to hold before the first marker leaves. */
  delay: number;
  chipBox?: [number, number, number, number];
  tap?: number;
};

/**
 * Where the ray enters and leaves its chip, as distances along the ray. Liang–
 * Barsky per segment, so it holds for the diagonal runs too rather than assuming
 * the crossing is axis aligned.
 */
function crossing(r: Ray): [number, number] | null {
  if (!r.chipBox) return null;
  const [bx, by, bw, bh] = r.chipBox;
  let travelled = 0;
  let enter = Infinity;
  let exit = -Infinity;
  for (let i = 1; i < r.pts.length; i += 1) {
    const [x0, y0] = r.pts[i - 1];
    const [x1, y1] = r.pts[i];
    const dx = x1 - x0;
    const dy = y1 - y0;
    const seg = Math.hypot(dx, dy);
    let t0 = 0;
    let t1 = 1;
    let clipped = true;
    for (const [p, q] of [
      [-dx, x0 - bx],
      [dx, bx + bw - x0],
      [-dy, y0 - by],
      [dy, by + bh - y0],
    ]) {
      if (p === 0) {
        if (q < 0) clipped = false;
      } else {
        const t = q / p;
        if (p < 0) t0 = Math.max(t0, t);
        else t1 = Math.min(t1, t);
      }
    }
    if (clipped && t0 <= t1) {
      enter = Math.min(enter, travelled + t0 * seg);
      exit = Math.max(exit, travelled + t1 * seg);
    }
    travelled += seg;
  }
  return enter <= exit ? [enter, exit] : null;
}

/** When the marker reaches this ray's chip, in seconds from t=0. */
function hitTime(r: Ray) {
  const { delay, len } = rayGeometry(r);
  const cross = crossing(r);
  return delay + (cross ? cross[0] : (r.tap ?? 1) * len) / SPEED;
}

/** The run of a polyline between two distances along it. */
function subPath(pts: [number, number][], from: number, to: number) {
  const at = (d: number): [number, number] => {
    let travelled = 0;
    for (let i = 1; i < pts.length; i += 1) {
      const seg = Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]);
      if (travelled + seg >= d) {
        const k = (d - travelled) / seg;
        return [
          pts[i - 1][0] + (pts[i][0] - pts[i - 1][0]) * k,
          pts[i - 1][1] + (pts[i][1] - pts[i - 1][1]) * k,
        ];
      }
      travelled += seg;
    }
    return pts[pts.length - 1];
  };
  const out: [number, number][] = [at(from)];
  let travelled = 0;
  for (let i = 1; i < pts.length; i += 1) {
    travelled += Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]);
    if (travelled > from && travelled < to) out.push(pts[i]);
  }
  out.push(at(to));
  return out.map(([x, y], i) => `${i ? "L" : "M"}${x.toFixed(2)} ${y.toFixed(2)}`).join("");
}

/**
 * Band coordinates, read off the connector assets and their placements: each
 * `line-*.svg` is drawn right-to-left, so these are the reversed runs, pointing
 * the way the intelligence actually flows.
 */
const RAYS: Ray[] = [
  {
    key: "feed",
    // line-partnership, x 195 y 90: research → the core's left edge
    pts: [
      [196.12, 91.18],
      [211.89, 91.18],
      [229.65, 108.92],
      [294.3, 108.92],
      [311.8, 91.88],
      [414.14, 91.88],
    ],
    delay: 0,
    // the 0-day / n-day chips straddle the line at x 357.6
    tap: 0.757,
  },
  {
    key: "top",
    // line-top, x 694 y 38: the upper node ⊙ → EXPLOIT PACK → Q-Suite
    pts: [
      [695.12, 62.89],
      [809.24, 62.89],
      [833.77, 38.36],
      [1089.16, 38.36],
    ],
    delay: 1.05,
    chipBox: [882.016, 24, 108.588, 29.08],
  },
  {
    key: "mid",
    // line-mid, x 802 y 119: the arrow plate → ATTACK SCENARIOS → HackGrid
    pts: [
      [803.05, 120.1],
      [1029.45, 120.1],
    ],
    delay: 1.35,
    chipBox: [858.986, 106, 154.648, 29.08],
  },
  {
    key: "bottom",
    // line-bottom, x 762 y 173: the lower node ⊙ → DETECTION ENGINEERING → MDR/SOC
    pts: [
      [763.12, 174.18],
      [807.28, 174.18],
      [831.81, 198.71],
      [1087.2, 198.71],
    ],
    delay: 1.65,
    chipBox: [844, 184, 184.228, 29.08],
  },
];

/**
 * The phone rays. Figma rotates all four connectors, so these endpoints are the
 * rotated ones — `rotated()` below is the same maths, and the results check out
 * against the layout boxes it was given (rail-upper lands on y 199.0 for a box
 * that starts at 199.02, conn-right on x 285.52/305.02 for a box of 285.52 + 19.5).
 */
const M_RAYS: Ray[] = [
  // rail-upper: research → the core
  {
    key: "feed",
    pts: [
      [195, 197.57],
      [195, 291.01],
    ],
    delay: 0,
    // the chips straddle the rail at y 245.1
    tap: 0.509,
  },
  // conn-left: the upper node ⊙ → EXPLOIT PACK → Q-Suite
  {
    key: "left",
    pts: [
      [158.03, 431.99],
      [137.46, 452.59],
      [74.98, 452.59],
      [58.01, 469.56],
      [58.01, 567],
    ],
    delay: 1.05,
    chipBox: [19.59, 489.68, 76.763, 20.7],
  },
  // rail-lower: the core → ATTACK SCENARIOS → the products row
  {
    key: "down",
    pts: [
      [195, 491.97],
      [195, 567],
    ],
    delay: 1.35,
    chipBox: [140.59, 532.64, 109.223, 20.7],
  },
  // conn-right: the lower node ⊙ → DETECTION ENGINEERING → MDR/SOC
  {
    key: "right",
    pts: [
      [285.52, 422.66],
      [305.02, 443.22],
      [305.02, 567],
    ],
    delay: 1.65,
    chipBox: [239.6, 489.68, 130.068, 20.7],
  },
];

/**
 * Band units a marker covers per second. Markers keep a constant *real* speed
 * rather than a shared cycle, so a short ray does not crawl next to a long one;
 * each ray's period therefore differs, and they drift apart over time — which is
 * fine, they feed different products and never read as one beat.
 */
const SPEED = 190;
/** Share of each cycle the ray spends empty, so markers arrive in ones. */
const GAP = 0.6;

/**
 * Radius of the travelling marker: one solid red disc, no ring, no tail, no halo.
 * Drawn as a zero-length dash with a round cap, so the disc is exactly the stroke
 * width across.
 *
 * Both alternatives were built and rejected on sight. A comet smeared red down
 * lines this long, and red is the one colour the artwork spends sparingly. A ring
 * with a hollow centre needs a stroke of about a unit to survive antialiasing at
 * these sizes, and even then it read as an outline rather than a marker.
 *
 * Roughly half the node ⊙ it leaves from, in each composition's own units: the
 * desktop node measures 15.6 across, the phone one 11.1.
 */
const DOT = 3.8;
const M_DOT = 2.7;

type Vars = React.CSSProperties & Record<string, string | number>;

function rayGeometry({ pts, delay }: Ray) {
  const d = pts.map(([x, y], i) => `${i ? "L" : "M"}${x} ${y}`).join("");
  let len = 0;
  for (let i = 1; i < pts.length; i += 1) {
    len += Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]);
  }
  const period = len * (1 + GAP);
  return { d, len, delay, period, dur: period / SPEED };
}

/**
 * One marker running one ray. It is a zero-length dash with a round cap, so it
 * paints as a disc exactly `2r` across and stays round through the elbows.
 *
 * A dash rather than a circle on `offset-path` because a ray that runs through an
 * output chip is drawn as two separate sub-paths — the marker must not cross the
 * label — and the two halves have to share one clock or the marker would arrive
 * out the far side at the wrong moment. A dash gives that for free: both halves
 * animate the same offset over the same period, and the second one is simply
 * shifted by the distance already covered, so the dash reaches it exactly when a
 * marker travelling at `SPEED` would have.
 *
 * The dash is only painted where it actually lies on its sub-path, so nothing has
 * to switch it off behind the chip; it runs out of path and stops existing.
 */
function Marker({ ray, r }: { ray: Ray; r: number }) {
  const { len, delay, period, dur } = rayGeometry(ray);
  const cross = crossing(ray);
  const runs: [string, number][] = cross
    ? [
        [subPath(ray.pts, 0, cross[0]), 0],
        [subPath(ray.pts, cross[1], len), cross[1]],
      ]
    : [[subPath(ray.pts, 0, len), 0]];
  return (
    <>
      {runs.map(([d, shift], i) => {
        const style: Vars = {
          strokeWidth: r * 2,
          strokeDasharray: `0.01 ${(period - 0.01).toFixed(2)}`,
          "--ph-o0": (period + shift).toFixed(2),
          "--ph-o1": shift.toFixed(2),
          animationDuration: `${dur.toFixed(3)}s`,
          animationDelay: `${delay}s`,
        };
        return <path key={i} className="ph-dot" d={d} style={style} />;
      })}
    </>
  );
}

/**
 * The pulse a node ⊙ gives off as it dispatches, and the flash an output chip
 * gives off as the marker lands on it. Both hang off their ray's own clock so
 * they cannot drift away from the marker that causes them.
 */
function Ping({ ray, cx, cy, r }: { ray: Ray; cx: number; cy: number; r: number }) {
  const { delay, dur } = rayGeometry(ray);
  return (
    <circle
      className="ph-ping"
      cx={cx}
      cy={cy}
      r={r}
      style={{ animationDuration: `${dur.toFixed(3)}s`, animationDelay: `${delay}s` }}
    />
  );
}

function ChipGlow({
  ray,
  d,
  x,
  y,
  at,
  width,
}: {
  ray: Ray;
  d: string;
  x: number;
  y: number;
  /** Override the moment it fires; the plate flashes on arrival, not at a chip. */
  at?: React.CSSProperties;
  /** Defaults to the chips' own weight; the plate's outline is heavier. */
  width?: number;
}) {
  const { dur } = rayGeometry(ray);
  return (
    <path
      className="ph-chip-glow"
      d={d}
      strokeWidth={width}
      transform={`translate(${x} ${y})`}
      style={
        at ?? {
          animationDuration: `${dur.toFixed(3)}s`,
          animationDelay: `${hitTime(ray).toFixed(3)}s`,
        }
      }
    />
  );
}

/**
 * The clock for a 0-DAY / N-DAY label. Both compositions run the feed down a
 * different length of line, so the brightening cannot be a fixed duration in the
 * stylesheet — it has to come off that layout's own feed ray or the chips stop
 * agreeing with the marker going past them.
 */
function dayTiming(feed: Ray, nudge: number): React.CSSProperties {
  return {
    animationDuration: `${rayGeometry(feed).dur.toFixed(3)}s`,
    animationDelay: `${(hitTime(feed) + nudge).toFixed(3)}s`,
  };
}

/** The clock for the product a ray ends at: the moment the marker lands on it. */
function arrivalTiming(r: Ray): React.CSSProperties {
  const { delay, len, dur } = rayGeometry(r);
  return {
    animationDuration: `${dur.toFixed(3)}s`,
    animationDelay: `${(delay + len / SPEED).toFixed(3)}s`,
  };
}

/**
 * The EXPLOIT INTELLIGENCE plate's own outline — core.svg id Vector_11 and its
 * phone twin Vector_9, transcribed so the frame around the project name can flash
 * the way the output chips do.
 *
 * It never crosses the lens: the outline's nearest approach to the lens centre is
 * 41.4 units against a disc of 36.2, so painting it over the whole core stack is
 * safe and it does not have to be threaded back between the split layers.
 */
const PLATE =
  "M27.845 148.837H171.795C173.385 148.837 174.905 149.467 176.035 150.587L186.515 161.057C187.635 162.177 189.165 162.807 190.755 162.807H239.545C241.105 162.807 242.605 162.197 243.725 161.107L256.185 149.387V103.827C256.185 102.287 255.585 100.797 254.525 99.687L233.225 77.417C232.095 76.237 230.525 75.567 228.895 75.567H181.505C179.915 75.567 178.395 74.937 177.265 73.817L166.785 63.347C165.665 62.227 164.135 61.597 162.545 61.597H31.815C30.255 61.597 28.755 62.207 27.635 63.297L17.335 73.317C16.215 74.407 14.715 75.017 13.155 75.017H6.535C3.225 75.017 0.535 77.697 0.535 81.017V110.287V120.587C0.535 122.127 1.135 123.617 2.195 124.727L23.495 146.997C24.625 148.177 26.195 148.847 27.825 148.847L27.845 148.837Z";
const M_PLATE =
  "M19.9938 106.398H122.56C123.693 106.398 124.776 106.849 125.581 107.649L133.048 115.134C133.846 115.934 134.937 116.385 136.07 116.385H170.833C171.945 116.385 173.013 115.949 173.811 115.169L182.689 106.791V74.2228C182.689 73.1219 182.262 72.0568 181.507 71.2633L166.33 55.3436C165.525 54.5001 164.406 54.0211 163.245 54.0211H129.479C128.346 54.0211 127.263 53.5708 126.458 52.7701L118.991 45.2857C118.193 44.485 117.102 44.0347 115.97 44.0347H22.8225C21.7109 44.0347 20.6422 44.4707 19.8441 45.2499L12.5052 52.4127C11.7072 53.1919 10.6385 53.628 9.52694 53.628H4.81009C2.45166 53.628 0.535 55.5438 0.535 57.9171V78.8407V86.2037C0.535 87.3045 0.962509 88.3697 1.71777 89.1631L16.8943 105.083C17.6995 105.926 18.8181 106.405 19.9795 106.405L19.9938 106.398Z";

/** The output chips all share one hexagon, drawn 1:1 inside its placement box. */
const CHIP = {
  exploitpack:
    "M8.59411 28.73H99.9941L108.184 14.54L99.9941 0.35H8.59411L0.404113 14.54L8.59411 28.73Z",
  attack: "M8.59411 28.73H146.054L154.244 14.54L146.054 0.35H8.59411L0.404113 14.54L8.59411 28.73Z",
  detection:
    "M8.60424 28.73H175.634L183.824 14.54L175.634 0.35H8.60424L0.404236 14.54L8.60424 28.73Z",
  exploitpackSm:
    "M6.17578 20.35H70.5873L76.359 10.35L70.5873 0.35H6.17578L0.404113 10.35L6.17578 20.35Z",
  attackSm:
    "M6.17578 20.35H103.047L108.818 10.35L103.047 0.35H6.17578L0.404113 10.35L6.17578 20.35Z",
  detectionSm:
    "M6.18295 20.35H123.893L129.664 10.35L123.893 0.35H6.18295L0.404236 10.35L6.18295 20.35Z",
};

/**
 * Figma rotates several phone-layout pieces, which `get_design_context` expresses
 * as "lay out a w × h box, put an h × w element in its centre, rotate that". This
 * rebuilds the same thing: `box` is the layout box, the returned rect is the
 * un-rotated element centred in it, and `spin` is the transform to hang on the
 * <g>. Note `get_metadata` reports the *bottom* edge as `y` for these nodes — the
 * numbers below all come from the design-context insets instead.
 */
function rotated(
  box: [number, number, number, number],
  { mirror = false, cw = false }: { mirror?: boolean; cw?: boolean } = {}
) {
  const [x, y, w, h] = box;
  const cx = x + w / 2;
  const cy = y + h / 2;
  const angle = cw ? 90 : -90;
  return {
    // the element swaps w/h because it is what gets rotated into the box
    rect: { x: cx - h / 2, y: cy - w / 2, w: h, h: w },
    spin: mirror
      ? `translate(${cx} ${cy}) rotate(${angle}) scale(-1 1) translate(${-cx} ${-cy})`
      : `rotate(${angle} ${cx} ${cy})`,
  };
}

/* Phone layout: the four rotated pieces. */
const M_ARROWS = rotated([179.01, 429, 31.69, 70.56], { mirror: true });
const M_CONN_RIGHT = rotated([285.52, 423.69, 19.5, 143.31]);
const M_CONN_LEFT = rotated([57.99, 432.97, 99.02, 134.03], {
  mirror: true,
  cw: true,
});
const M_RAIL_UPPER = rotated([195, 199.02, 0, 91.99]);
const M_RAIL_LOWER = rotated([195, 491.97, 0, 75.03]);

/** A chip outline plus its centred label, used by the phone layout. */
function MobileChip({
  src,
  box,
  label,
}: {
  src: string;
  box: [number, number, number, number];
  label: string;
}) {
  const [x, y, w, h] = box;
  return (
    <>
      <image href={src} x={x} y={y} width={w} height={h} preserveAspectRatio="none" />
      <text
        x={x + w / 2}
        y={y + h / 2}
        fontSize="8"
        fontWeight="600"
        fill="#f9fcfe"
        textAnchor="middle"
        dominantBaseline="central"
      >
        {label}
      </text>
    </>
  );
}

/** One split-out core layer, laid into its composition's box. */
function CoreLayer({
  prefix,
  name,
  box,
  className,
  style,
}: {
  prefix: string;
  name: string;
  box: { x: number; y: number; w: number; h: number };
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <image
      className={className}
      style={style}
      href={`${ART}/${prefix}-${name}.svg`}
      x={box.x}
      y={box.y}
      width={box.w}
      height={box.h}
      preserveAspectRatio="none"
    />
  );
}

export default function ProductionHouseArt({
  playing = false,
}: {
  /** True only while this banner is the active slide — see the note up top. */
  playing?: boolean;
}) {
  const play = playing ? "true" : "false";
  const ray = (key: string) => RAYS.find((r) => r.key === key) as Ray;
  const mRay = (key: string) => M_RAYS.find((r) => r.key === key) as Ray;

  return (
    <div className="w-full">
      {/* ── lg and up: the desktop band, 1:1 with Figma ───────────────── */}
      <svg
        /* overflow-visible because two of the three product lockups are flush
           against the viewBox: Q-Suite starts at y=0 and HackGrid ends at
           x=1292.14, so the arrival pulse grows them straight past the edge and
           the default overflow:hidden slices the top off the Q mark and the tail
           off CYBER RANGE. The few units they spill land in the banner's own
           gutter. */
        className="font-avenir hidden h-auto w-full overflow-visible lg:block"
        viewBox={`0 0 ${BAND_W} ${BAND_H}`}
        role="img"
        aria-label={ART_LABEL}
        data-playing={play}
      >
        <defs>
          {/* The wipe that lays the red bar in under INTELLIGENCE. Scaling a rect
              about its own left edge needs no user-unit origin, so this survives
              the viewBox without any transform-box guesswork. */}
          <clipPath id="ph-bar-wipe">
            <rect className="ph-bar-wipe" x="442" y="94" width="152" height="34" />
          </clipPath>
        </defs>

        {/* Research feeds — SHRED × KhaiCode */}
        <image
          href="/images/Home/shred.svg"
          x="0"
          y="6"
          width="203.675"
          height="68.08"
          preserveAspectRatio="none"
        />
        {/* textLength pins the run to the Figma width so it can never grow past
            the connector that starts at x=195. */}
        <text
          x="2"
          y="92.99"
          fontSize="17.2"
          fontWeight="600"
          fill="#ffffff"
          dominantBaseline="central"
          textLength="182"
          lengthAdjust="spacing"
        >
          IN PARTNERSHIP WITH
        </text>
        <image
          href={`${ART}/line-partnership.svg`}
          x="195"
          y="90"
          width="220.26"
          height="19.28"
          preserveAspectRatio="none"
        />
        <image
          href={`${ART}/khaicode.svg`}
          x="2"
          y="128"
          width="269.69"
          height="33.09"
          preserveAspectRatio="none"
        />

        {/* Inputs — 0-day / n-day. The chips are not wired to the feed line, they
            straddle it, so they brighten as the marker goes by rather than
            passing it along. */}
        <image
          href={`${ART}/chip-day.svg`}
          x="322"
          y="46"
          width="71.23"
          height="29.42"
          preserveAspectRatio="none"
        />
        <text
          className="ph-day"
          style={{ ...dayTiming(ray("feed"), -0.02), color: "#e8233f" }}
          x="357.615"
          y="60.71"
          fontSize="11.2"
          fontWeight="600"
          textAnchor="middle"
          dominantBaseline="central"
          letterSpacing="0.4"
        >
          0-DAY
        </text>
        <image
          href={`${ART}/chip-day.svg`}
          x="322"
          y="106"
          width="71.23"
          height="29.42"
          preserveAspectRatio="none"
        />
        <text
          className="ph-day"
          style={{ ...dayTiming(ray("feed"), 0.06), color: "#00adbe" }}
          x="357.615"
          y="120.71"
          fontSize="11.2"
          fontWeight="600"
          textAnchor="middle"
          dominantBaseline="central"
          letterSpacing="0.4"
        >
          N-DAY
        </text>

        {/* The Exploit Intelligence core, layer by layer */}
        {CORE_LAYERS.map((name) => (
          <CoreLayer
            key={name}
            prefix="core"
            name={name}
            box={CORE.box}
            className={name === "arcs" ? "ph-arcs" : name === "reticle" ? "ph-reticle" : undefined}
            style={
              name === "arcs"
                ? { transformOrigin: CORE.arc }
                : name === "reticle"
                  ? { transformOrigin: CORE.dial }
                  : undefined
            }
          />
        ))}
        {/* A charge running the dial's tick ring. The ticks only cover the left
            half of the circle (88° to 272°), so this is the large arc, taken
            anticlockwise. */}
        <path className="ph-dial" d={CORE.tickArc} pathLength="100" strokeWidth={CORE.dialWidth} />

        {/* The frame around the project name flashes on the same terms as the
            output chips do — it is the same class and the same keyframe — timed
            to the feed marker arriving at the core. */}
        <ChipGlow
          ray={ray("feed")}
          d={PLATE}
          x={CORE.box.x}
          y={CORE.box.y}
          width={1.3}
          at={arrivalTiming(ray("feed"))}
        />

        {/* The two nodes the outputs leave from (core.svg ids 138–142 and 170),
            transcribed so each can pulse as its ray fires. Transcriptions keep
            the core's own coordinates and the group carries them into the band,
            so they can be diffed against the source file as they stand. */}
        <g transform={`translate(${CORE.box.x} ${CORE.box.y})`}>
          <path
            d="M318.285 188.817C309.825 188.817 302.955 181.937 302.955 173.477C302.955 165.017 309.835 158.137 318.285 158.137C326.735 158.137 333.615 165.017 333.615 173.477C333.615 181.937 326.735 188.817 318.285 188.817Z"
            fill="#15181C"
          />
          <path
            d="M318.285 160.547C325.425 160.547 331.215 166.337 331.215 173.477C331.215 180.617 325.425 186.407 318.285 186.407C311.145 186.407 305.355 180.617 305.355 173.477C305.355 166.337 311.145 160.547 318.285 160.547ZM318.285 155.747C308.505 155.747 300.545 163.707 300.545 173.487C300.545 183.267 308.505 191.227 318.285 191.227C328.065 191.227 336.025 183.267 336.025 173.487C336.025 163.707 328.065 155.747 318.285 155.747Z"
            fill="#585965"
          />
          <path
            d="M260.585 71.697C264.307 71.697 267.325 68.6794 267.325 64.957C267.325 61.2346 264.307 58.217 260.585 58.217C256.863 58.217 253.845 61.2346 253.845 64.957C253.845 68.6794 256.863 71.697 260.585 71.697Z"
            fill="#15181C"
          />
          <path
            d="M260.585 59.267C263.725 59.267 266.275 61.817 266.275 64.957C266.275 68.097 263.725 70.647 260.585 70.647C257.445 70.647 254.895 68.097 254.895 64.957C254.895 61.817 257.445 59.267 260.585 59.267ZM260.585 57.157C256.285 57.157 252.785 60.657 252.785 64.957C252.785 69.257 256.285 72.757 260.585 72.757C264.885 72.757 268.385 69.257 268.385 64.957C268.385 60.657 264.885 57.157 260.585 57.157Z"
            fill="#E8233F"
          />
          <path
            d="M260.585 67.507C261.993 67.507 263.135 66.3653 263.135 64.957C263.135 63.5487 261.993 62.407 260.585 62.407C259.177 62.407 258.035 63.5487 258.035 64.957C258.035 66.3653 259.177 67.507 260.585 67.507Z"
            fill="#E8233F"
          />
          <Ping ray={ray("top")} cx={260.585} cy={64.957} r={7.8} />
          <Ping ray={ray("bottom")} cx={318.285} cy={173.487} r={17.74} />
        </g>

        <CoreLayer
          prefix="core"
          name="bar"
          box={CORE.box}
          style={{ clipPath: "url(#ph-bar-wipe)" }}
        />
        <CoreLayer prefix="core" name="glyphs" box={CORE.box} />

        {/* The ››› plate (core.svg ids 164–169), transcribed so the three
            chevrons can chase towards the mid output. */}
        <g transform={`translate(${CORE.box.x} ${CORE.box.y})`}>
          <g className="ph-chev ph-chev-1">
            <path d="M311.215 112.887L317.035 118.707L311.215 124.527" />
            <path d="M316.785 118.717H305.515" />
          </g>
          <g className="ph-chev ph-chev-2">
            <path d="M331.115 112.887L336.935 118.707L331.115 124.527" />
            <path d="M336.685 118.717H325.425" />
          </g>
          <g className="ph-chev ph-chev-3">
            <path d="M351.015 112.887L356.835 118.707L351.015 124.527" />
            <path d="M356.585 118.717H345.325" />
          </g>
          <path
            d="M318.285 177.527C320.516 177.527 322.325 175.718 322.325 173.487C322.325 171.256 320.516 169.447 318.285 169.447C316.054 169.447 314.245 171.256 314.245 173.487C314.245 175.718 316.054 177.527 318.285 177.527Z"
            fill="#E8233F"
          />
        </g>

        {/* Connectors out to the three outputs */}
        <image
          href={`${ART}/line-top.svg`}
          x="694"
          y="38"
          width="395.16"
          height="26.06"
          preserveAspectRatio="none"
        />
        <image
          href={`${ART}/line-mid.svg`}
          x="802"
          y="119"
          width="227.45"
          height="2.21"
          preserveAspectRatio="none"
        />
        <image
          href={`${ART}/line-bottom.svg`}
          x="762"
          y="173"
          width="325.2"
          height="26.07"
          preserveAspectRatio="none"
        />

        {/* Outputs — painted after the connectors so the chips sit on top */}
        <image
          href={`${ART}/chip-exploitpack.svg`}
          x="882.016"
          y="24"
          width="108.588"
          height="29.08"
          preserveAspectRatio="none"
        />
        <text
          x="936.31"
          y="38.54"
          fontSize="12.69"
          fontWeight="600"
          fill="#f9fcfe"
          textAnchor="middle"
          dominantBaseline="central"
        >
          EXPLOIT PACK
        </text>
        <image
          href={`${ART}/chip-attack.svg`}
          x="858.986"
          y="106"
          width="154.648"
          height="29.08"
          preserveAspectRatio="none"
        />
        <text
          x="936.31"
          y="120.54"
          fontSize="12.69"
          fontWeight="600"
          fill="#f9fcfe"
          textAnchor="middle"
          dominantBaseline="central"
        >
          ATTACK SCENARIOS
        </text>
        <image
          href={`${ART}/chip-detection.svg`}
          x="844"
          y="184"
          width="184.228"
          height="29.08"
          preserveAspectRatio="none"
        />
        <text
          x="936.11"
          y="198.54"
          fontSize="12.69"
          fontWeight="600"
          fill="#f9fcfe"
          textAnchor="middle"
          dominantBaseline="central"
        >
          DETECTION ENGINEERING
        </text>

        {/* Markers, and the flashes they set off on the way past. Drawn last so a
            head never disappears under a chip it is crossing. */}
        <g className="ph-flow">
          {RAYS.map((r) => (
            <Marker key={r.key} ray={r} r={DOT} />
          ))}
          <ChipGlow ray={ray("top")} d={CHIP.exploitpack} x={882.016} y={24} />
          <ChipGlow ray={ray("mid")} d={CHIP.attack} x={858.986} y={106} />
          <ChipGlow ray={ray("bottom")} d={CHIP.detection} x={844} y={184} />
        </g>

        {/* Products the intelligence feeds */}
        <g className="ph-product" style={arrivalTiming(ray("top"))}>
          <image
            href={`${ART}/qsuite.svg`}
            x="1108"
            y="0"
            width="130.13"
            height="47.78"
            preserveAspectRatio="none"
          />
        </g>
        <g className="ph-product" style={arrivalTiming(ray("mid"))}>
          <image
            href={`${ART}/hackgrid.svg`}
            x="1053"
            y="99"
            width="239.14"
            height="46.62"
            preserveAspectRatio="none"
          />
        </g>
        <g className="ph-product" style={arrivalTiming(ray("bottom"))}>
          <text
            x="1102.79"
            y="196.92"
            fontSize="14.2"
            fontWeight="600"
            fill="#f9fcfe"
            dominantBaseline="central"
          >
            MDR / SOC
          </text>
          <image
            href={`${ART}/mdr-soc.svg`}
            x="1192.74"
            y="166"
            width="63.22"
            height="63.22"
            preserveAspectRatio="none"
          />
        </g>
      </svg>

      {/* ── below lg: the phone composition (node 2612:38704, redrawn 103px
             lower as node 2709:38384 once the headline moved above it — the
             composition itself is unchanged, so this artboard window still
             holds) ──────────────────────────────────────────────────────── */}
      <svg
        /* max-h keeps short phones honest: the SVG has an intrinsic ratio, so a
           height cap shrinks the width with it. The 330px budget is the overlaid
           header (60) + the headline (80) + its gap (34) + the bottom slide dots
           (48) + breathing room. */
        className="font-avenir mx-auto block h-auto max-h-[calc(100svh-330px)] w-full max-w-[390px] overflow-visible sm:max-w-[420px] md:max-w-[460px] lg:hidden"
        viewBox={M_VIEWBOX}
        role="img"
        aria-label={ART_LABEL}
        data-playing={play}
      >
        <defs>
          <clipPath id="ph-m-bar-wipe">
            <rect className="ph-bar-wipe" x="58" y="367" width="108" height="26" />
          </clipPath>
        </defs>

        {/* The Exploit Intelligence core, layer by layer */}
        {CORE_LAYERS.map((name) => (
          <CoreLayer
            key={name}
            prefix="core-mobile"
            name={name}
            box={M_CORE.box}
            className={name === "arcs" ? "ph-arcs" : name === "reticle" ? "ph-reticle" : undefined}
            style={
              name === "arcs"
                ? { transformOrigin: M_CORE.arc }
                : name === "reticle"
                  ? { transformOrigin: M_CORE.dial }
                  : undefined
            }
          />
        ))}
        {/* The same charge running the dial ticks as the desktop core has. It is
            worth having here too: the ring is 4.8% of this artboard's width
            against 2.0% of the band's, so it reads more clearly, not less. */}
        <path
          className="ph-dial"
          d={M_CORE.tickArc}
          pathLength="100"
          strokeWidth={M_CORE.dialWidth}
        />

        {/* The frame around the project name — see the desktop note above. */}
        <ChipGlow
          ray={mRay("feed")}
          d={M_PLATE}
          x={M_CORE.box.x}
          y={M_CORE.box.y}
          width={1.3}
          at={arrivalTiming(mRay("feed"))}
        />

        {/* The upper node ⊙ (core-mobile.svg ids 136–138) */}
        <path
          d="M120.715 131.394C123.367 131.394 125.517 129.237 125.517 126.576C125.517 123.915 123.367 121.758 120.715 121.758C118.063 121.758 115.913 123.915 115.913 126.576C115.913 129.237 118.063 131.394 120.715 131.394Z"
          fill="#15181C"
          transform="translate(42.48 300.97)"
        />
        <path
          d="M120.715 122.508C122.952 122.508 124.769 124.331 124.769 126.576C124.769 128.82 122.952 130.643 120.715 130.643C118.478 130.643 116.661 128.82 116.661 126.576C116.661 124.331 118.478 122.508 120.715 122.508ZM120.715 121C117.651 121 115.157 123.502 115.157 126.576C115.157 129.649 117.651 132.151 120.715 132.151C123.779 132.151 126.273 129.649 126.273 126.576C126.273 123.502 123.779 121 120.715 121Z"
          fill="#E8233F"
          transform="translate(42.48 300.97)"
        />
        <path
          d="M120.715 128.399C121.718 128.399 122.532 127.582 122.532 126.576C122.532 125.569 121.718 124.753 120.715 124.753C119.711 124.753 118.898 125.569 118.898 126.576C118.898 127.582 119.711 128.399 120.715 128.399Z"
          fill="#E8233F"
          transform="translate(42.48 300.97)"
        />
        <Ping ray={mRay("left")} cx={163.195} cy={427.546} r={5.558} />

        <CoreLayer
          prefix="core-mobile"
          name="bar"
          box={M_CORE.box}
          style={{ clipPath: "url(#ph-m-bar-wipe)" }}
        />
        <CoreLayer prefix="core-mobile" name="glyphs" box={M_CORE.box} />

        {/* The lower node ⊙ (core-mobile.svg ids 160–162) */}
        <path
          d="M232.175 123.64C226.147 123.64 221.252 118.722 221.252 112.674C221.252 106.627 226.154 101.708 232.175 101.708C238.196 101.708 243.098 106.627 243.098 112.674C243.098 118.722 238.196 123.64 232.175 123.64Z"
          fill="#15181C"
          transform="translate(42.48 300.97)"
        />
        <path
          d="M232.175 103.431C237.262 103.431 241.388 107.57 241.388 112.674C241.388 117.778 237.262 121.917 232.175 121.917C227.088 121.917 222.962 117.778 222.962 112.674C222.962 107.57 227.088 103.431 232.175 103.431ZM232.175 99.9999C225.207 99.9999 219.535 105.69 219.535 112.681C219.535 119.673 225.207 125.363 232.175 125.363C239.143 125.363 244.815 119.673 244.815 112.681C244.815 105.69 239.143 99.9999 232.175 99.9999Z"
          fill="#585965"
          transform="translate(42.48 300.97)"
        />
        <path
          d="M232.175 115.569C233.765 115.569 235.054 114.276 235.054 112.681C235.054 111.086 233.765 109.793 232.175 109.793C230.585 109.793 229.296 111.086 229.296 112.681C229.296 114.276 230.585 115.569 232.175 115.569Z"
          fill="#E8233F"
          transform="translate(42.48 300.97)"
        />
        <Ping ray={mRay("right")} cx={274.655} cy={413.651} r={12.64} />

        {/* ↓↓↓ — the desktop plate's arrows, turned to run downwards. Inlined
            (arrows.svg ids Vector..Vector_8) so the three heads can chase; the
            scale is the 70.5461 × 31.7322 artboard meeting its 70.56 × 31.69 box. */}
        <g transform={M_ARROWS.spin}>
          <g
            transform={`translate(${M_ARROWS.rect.x} ${M_ARROWS.rect.y}) scale(${(
              M_ARROWS.rect.w / 70.5461
            ).toFixed(6)} ${(M_ARROWS.rect.h / 31.7322).toFixed(6)})`}
          >
            <path
              d="M0 15.9197V5.59728H16.1242L21.8528 0H42.0954L47.9095 5.82603H63.2357C66.0929 8.69257 67.6889 10.2938 70.5461 13.1604V15.9197H0Z"
              fill="#15181C"
            />
            <path
              d="M0 15.8125V26.1349H16.1242L21.8528 31.7322H42.0954L47.9095 25.9062H63.2357C66.0929 23.0396 67.6889 21.4384 70.5461 18.5718V15.8125H0Z"
              fill="#15181C"
            />
            {/* Chase runs top to bottom on screen, which the mirror-and-turn maps
                back to increasing x here. */}
            <g className="ph-chev ph-chev-1">
              <path d="M19.2877 11.5734L23.4346 15.7339L19.2877 19.8943" />
              <path d="M23.2565 15.741H15.2264" />
            </g>
            <g className="ph-chev ph-chev-2">
              <path d="M33.4667 11.5734L37.6135 15.7339L33.4667 19.8943" />
              <path d="M37.4355 15.741H29.4126" />
            </g>
            <g className="ph-chev ph-chev-3">
              <path d="M47.6458 11.5734L51.7926 15.7339L47.6458 19.8943" />
              <path d="M51.6145 15.741H43.5916" />
            </g>
          </g>
        </g>

        {/* Inputs — 0-day / n-day */}
        <image
          href={`${ART}/chip-day.svg`}
          x="113.475"
          y="230.415"
          width="71.23"
          height="29.42"
          preserveAspectRatio="none"
        />
        <text
          className="ph-day"
          style={{ ...dayTiming(mRay("feed"), -0.02), color: "#e8233f" }}
          x="149.095"
          y="245.125"
          fontSize="11.2"
          fontWeight="600"
          textAnchor="middle"
          dominantBaseline="central"
          letterSpacing="0.4"
        >
          0-DAY
        </text>
        <image
          href={`${ART}/chip-day.svg`}
          x="205.475"
          y="230.415"
          width="71.23"
          height="29.42"
          preserveAspectRatio="none"
        />
        <text
          className="ph-day"
          style={{ ...dayTiming(mRay("feed"), 0.06), color: "#00adbe" }}
          x="241.095"
          y="245.125"
          fontSize="11.2"
          fontWeight="600"
          textAnchor="middle"
          dominantBaseline="central"
          letterSpacing="0.4"
        >
          N-DAY
        </text>

        {/* Research feeds */}
        <image
          href="/images/Home/shred.svg"
          x="34.01"
          y="152.89"
          width="137.62"
          height="46.09"
          preserveAspectRatio="none"
        />
        <text
          x="207.99"
          y="160.53"
          fontSize="10"
          fontWeight="600"
          fill="#ffffff"
          dominantBaseline="central"
        >
          IN PARTNERSHIP WITH
        </text>
        <image
          href={`${ART}/khaicode.svg`}
          x="207.99"
          y="174.03"
          width="152.99"
          height="18.74"
          preserveAspectRatio="none"
        />

        {/* Products the intelligence feeds. Both logos hang off the same top
            edge, y 584, exactly as Figma has them, which leaves the three
            lockups level with each other (measured ink centres 597.0 / 597.0 /
            597.2). Q-Suite carries its wordmark on the lower line, so SUiTE
            reads alongside CYBER RANGE while HACKGRiD sits a line higher —
            that is the design, and lifting the lockup to level the two
            wordmarks was tried and rejected: it takes 16, which pushes the Q
            mark clear of the row. Leave both boxes where they are. */}
        <g className="ph-product" style={arrivalTiming(mRay("left"))}>
          <image
            href={`${ART}/qsuite.svg`}
            x="25"
            y="584"
            width="66.54"
            height="24.43"
            preserveAspectRatio="none"
          />
        </g>
        <g className="ph-product" style={arrivalTiming(mRay("down"))}>
          <image
            href={`${ART}/hackgrid.svg`}
            x="134"
            y="584"
            width="122.29"
            height="23.84"
            preserveAspectRatio="none"
          />
        </g>
        <g className="ph-product" style={arrivalTiming(mRay("right"))}>
          <text
            x="306"
            y="594.09"
            fontSize="10"
            fontWeight="600"
            fill="#f9fcfe"
            textAnchor="middle"
            dominantBaseline="central"
          >
            MDR / SOC
          </text>
          <image
            href={`${ART}/mdr-soc.svg`}
            x="337.47"
            y="580"
            width="32.33"
            height="32.33"
            preserveAspectRatio="none"
          />
        </g>

        {/* Connectors — drawn over the core and logos, under the chips */}
        <g transform={M_RAIL_UPPER.spin}>
          <image
            href={`${ART}/rail-upper.svg`}
            x={M_RAIL_UPPER.rect.x}
            y={M_RAIL_UPPER.rect.y - 1.44}
            width="93.4434"
            height="2.88675"
            preserveAspectRatio="none"
          />
        </g>
        <g transform={M_RAIL_LOWER.spin}>
          <image
            href={`${ART}/rail-lower.svg`}
            x={M_RAIL_LOWER.rect.x}
            y={M_RAIL_LOWER.rect.y - 1.44}
            width="76.4434"
            height="2.88675"
            preserveAspectRatio="none"
          />
        </g>
        <g transform={M_CONN_RIGHT.spin}>
          <image
            href={`${ART}/conn-right.svg`}
            x={M_CONN_RIGHT.rect.x}
            y={M_CONN_RIGHT.rect.y - 1.02}
            width="144.342"
            height="20.7705"
            preserveAspectRatio="none"
          />
        </g>
        <g transform={M_CONN_LEFT.spin}>
          <image
            href={`${ART}/conn-left.svg`}
            x={M_CONN_LEFT.rect.x}
            y={M_CONN_LEFT.rect.y - 1.02}
            width="135.021"
            height="100.271"
            preserveAspectRatio="none"
          />
        </g>

        {/* Outputs */}
        <MobileChip
          src={`${ART}/chip-detection-sm.svg`}
          box={[239.6, 489.68, 130.068, 20.7]}
          label="DETECTION ENGINEERING"
        />
        <MobileChip
          src={`${ART}/chip-exploitpack-sm.svg`}
          box={[19.59, 489.68, 76.763, 20.7]}
          label="EXPLOIT PACK"
        />
        <MobileChip
          src={`${ART}/chip-attack-sm.svg`}
          box={[140.59, 532.64, 109.223, 20.7]}
          label="ATTACK SCENARIOS"
        />

        <g className="ph-flow">
          {M_RAYS.map((r) => (
            <Marker key={r.key} ray={r} r={M_DOT} />
          ))}
          <ChipGlow ray={mRay("left")} d={CHIP.exploitpackSm} x={19.59} y={489.68} />
          <ChipGlow ray={mRay("down")} d={CHIP.attackSm} x={140.59} y={532.64} />
          <ChipGlow ray={mRay("right")} d={CHIP.detectionSm} x={239.6} y={489.68} />
        </g>
      </svg>

      <style jsx>{`
        /* Nothing below applies until the banner is the active slide, so the
           resting frame is the static design and reduced-motion needs no undo
           beyond the block at the end. */

        svg :global(.ph-dot) {
          fill: none;
          stroke: #e8233f;
          stroke-linecap: round;
          opacity: 0;
        }

        svg :global(.ph-ping) {
          fill: none;
          stroke: #e8233f;
          stroke-width: 1.1;
          opacity: 0;
          transform-box: fill-box;
          transform-origin: center;
        }

        /* 0.9 against the chips' own 0.7: enough to cover the grey outline
           underneath rather than fringe through it, and not enough to change the
           frame's weight. 1.6 was tried and read as the label being squeezed —
           the text never moved, the frame around it more than doubled. */
        svg :global(.ph-chip-glow) {
          fill: none;
          stroke: #e8233f;
          stroke-width: 0.9;
          opacity: 0;
        }

        /* currentColor so one animation on the group repaints both strokes; the
           resting colour is the designed white, and the chase only ever adds red
           on top of it. */
        .ph-chev {
          color: #ffffff;
        }
        .ph-chev path {
          fill: none;
          stroke: currentColor;
          stroke-width: 3.58;
          stroke-miterlimit: 10;
        }

        /* Dash lengths are in the pathLength=100 space, so the same numbers hold
           for both rings: the charge covers 14.4% of the arc and the period is
           196% of it, which leaves the ring dark for about half the cycle. */
        .ph-dial {
          fill: none;
          stroke: #e8233f;
          stroke-linecap: butt;
          stroke-dasharray: 14.4 181.5;
          opacity: 0;
        }

        /* ── running ─────────────────────────────────────────────────── */

        svg[data-playing="true"] :global(.ph-dot) {
          opacity: 1;
          animation-name: phDot;
          animation-timing-function: linear;
          animation-iteration-count: infinite;
        }

        /* The dash period is longer than the ray, so the marker spends the tail of
           each cycle past the end of the path — which is where the empty gap
           between markers comes from. --ph-o0 / --ph-o1 carry the per-sub-path
           head start, the one thing that differs between the two halves. */
        @keyframes phDot {
          from {
            stroke-dashoffset: var(--ph-o0);
          }
          to {
            stroke-dashoffset: var(--ph-o1);
          }
        }

        svg[data-playing="true"] :global(.ph-ping) {
          animation-name: phPing;
          animation-timing-function: ease-out;
          animation-iteration-count: infinite;
        }
        @keyframes phPing {
          0% {
            opacity: 0.75;
            scale: 0.94;
          }
          22% {
            opacity: 0;
            scale: 2.1;
          }
          100% {
            opacity: 0;
            scale: 2.1;
          }
        }

        svg[data-playing="true"] :global(.ph-chip-glow) {
          animation-name: phChip;
          animation-timing-function: ease-out;
          animation-iteration-count: infinite;
        }
        @keyframes phChip {
          0% {
            opacity: 0;
          }
          8% {
            opacity: 0.9;
          }
          40% {
            opacity: 0;
          }
          100% {
            opacity: 0;
          }
        }

        /* The red arcs are the only concentric pieces in the core, so they are
           the only ones that can turn without dragging the bezel's designed gap
           away from the plate that cuts across it. */
        svg[data-playing="true"] :global(.ph-arcs) {
          transform-box: fill-box;
          animation: phArcs 9s ease-in-out infinite;
        }
        @keyframes phArcs {
          0%,
          6% {
            rotate: -17deg;
          }
          47%,
          53% {
            rotate: 17deg;
          }
          94%,
          100% {
            rotate: -17deg;
          }
        }

        svg[data-playing="true"] :global(.ph-reticle) {
          transform-box: fill-box;
          animation: phFocus 4.2s ease-in-out infinite;
        }
        @keyframes phFocus {
          0%,
          100% {
            scale: 1;
          }
          50% {
            scale: 1.07;
          }
        }

        svg[data-playing="true"] .ph-dial {
          opacity: 0.85;
          animation: phDial 6s linear infinite;
        }
        @keyframes phDial {
          from {
            stroke-dashoffset: 195.9;
          }
          to {
            stroke-dashoffset: 0;
          }
        }

        svg[data-playing="true"] .ph-chev {
          animation: phChev 1.7s ease-in-out infinite;
        }
        svg[data-playing="true"] .ph-chev-2 {
          animation-delay: 0.16s;
        }
        svg[data-playing="true"] .ph-chev-3 {
          animation-delay: 0.32s;
        }
        /* The chase is a colour, not a dimmer: each head turns red in turn from
           the first to the last and hands back to white, so the arrows keep their
           designed weight the whole way through. */
        /* The red window (17–24% of 1.7s = 0.12s) has to be shorter than the 0.16s
           between heads, or all three sit red at once and it reads as one flash
           instead of something travelling. */
        @keyframes phChev {
          0%,
          12%,
          30%,
          100% {
            color: #ffffff;
          }
          17%,
          24% {
            color: #e8233f;
          }
        }

        /* The chips straddle the feed line rather than sitting on it, so they
           acknowledge the marker instead of receiving it. */
        .ph-day {
          fill: currentColor;
        }

        svg[data-playing="true"] .ph-day {
          animation-name: phDay;
          animation-timing-function: ease-out;
          animation-iteration-count: infinite;
        }
        /* A glow in the label's own hue, not brightness. Brightness is worthless
           on a channel that is already at maximum: 0-DAY is rgb(232,35,63), so
           brightening it only clamps red to 255 and the flash is invisible, while
           N-DAY at rgb(0,173,190) jumps to full cyan. The glow reads the same on
           both. Adding rather than dimming keeps the resting frame exact. */
        @keyframes phDay {
          0%,
          38%,
          100% {
            filter: brightness(1) drop-shadow(0 0 0 currentColor);
          }
          10% {
            filter: brightness(1.2) drop-shadow(0 0 2.5px currentColor);
          }
        }

        /* One-shots. A fill mode of both holds the finished frame, so once the
           first pass is over these look exactly like the static design again. */
        svg[data-playing="true"] .ph-bar-wipe {
          transform-box: fill-box;
          transform-origin: left center;
          animation: phWipe 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.35s both;
        }
        @keyframes phWipe {
          from {
            scale: 0 1;
          }
          to {
            scale: 1 1;
          }
        }

        /* Each product acknowledges its own delivery, on that ray's clock, so
           the pulse can never run ahead of the marker that earns it. It is a
           pulse rather than a fade-in for the same reason the chip labels are:
           the logo has to sit at its designed weight the rest of the time, and
           a 7s banner cannot afford two seconds of washed-out lockups first. */
        svg[data-playing="true"] .ph-product {
          transform-box: fill-box;
          transform-origin: center;
          animation-name: phProduct;
          animation-timing-function: ease-out;
          animation-iteration-count: infinite;
        }
        /* Scale only. Brightness was tried and does nothing here — Q-Suite and
           MDR/SOC are white artwork, so multiplying a channel already at 255
           leaves them untouched, and only HackGrid (a red rule, a grey second
           line) appeared to respond. A size pulse is the one acknowledgement
           that lands the same on all three whatever they are made of, and it
           returns the lockups to their designed weight exactly. */
        @keyframes phProduct {
          0%,
          34%,
          100% {
            scale: 1;
          }
          9% {
            scale: 1.07;
          }
        }

        /* globals.css only silences a handful of named effects, so this has to
           opt out here. Everything is already gated on data-playing, so killing
           the animations is enough — the markers and the swept dial are
           the exception: standing still they read as stray marks on the artwork,
           so they go entirely. */
        @media (prefers-reduced-motion: reduce) {
          svg[data-playing="true"] :global(.ph-dot),
          svg[data-playing="true"] :global(.ph-ping),
          svg[data-playing="true"] :global(.ph-chip-glow),
          svg[data-playing="true"] .ph-dial {
            display: none;
          }
          svg[data-playing="true"] :global(.ph-arcs),
          svg[data-playing="true"] :global(.ph-reticle),
          svg[data-playing="true"] .ph-chev,
          svg[data-playing="true"] .ph-day,
          svg[data-playing="true"] .ph-bar-wipe,
          svg[data-playing="true"] .ph-product {
            animation: none;
            opacity: 1;
            filter: none;
            rotate: none;
            scale: none;
            translate: none;
          }
        }
      `}</style>
    </div>
  );
}
